import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChefHat, TrendingUp, Truck } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { API } from "../../lib/apiClient";

const KITCHEN_COLORS = ["#2e6b31", "#d98a3d", "#3d7ad9", "#a44dd1", "#d1483d", "#8a998a"];

export function TrendsView() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ stock_levels: [], dispatch_volume: [] });
  const [kitchenData, setKitchenData] = useState({ usage_by_day: [], sections: [] });
  const [loading, setLoading] = useState(true);
  const requestId = useRef(0);

  useEffect(() => {
    setLoading(true);
    const thisRequest = ++requestId.current;
    Promise.allSettled([
      axios.get(`${API}/reports/trends?days=${days}`),
      axios.get(`${API}/reports/kitchen-usage-trend?days=${days}`),
    ]).then(([trendsRes, kitchenRes]) => {
      if (requestId.current !== thisRequest) return; // a newer range was selected, ignore this stale response
      if (trendsRes.status === "fulfilled") setData(trendsRes.value.data); else toast.error("Could not load stock/dispatch trends");
      if (kitchenRes.status === "fulfilled") setKitchenData(kitchenRes.value.data); else toast.error("Could not load kitchen usage trend");
    }).finally(() => { if (requestId.current === thisRequest) setLoading(false); });
  }, [days]);

  const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

  return <div className="page-wrap"><div className="page-heading"><div><p className="eyebrow">Analytics</p><h1>Trends</h1><p className="subheading">Stock levels, dispatch volume and kitchen usage over time.</p></div>
    <div className="filter-tabs">{[7, 30, 90].map((d) => <button key={d} data-testid={`trends-range-${d}`} className={days === d ? "selected" : ""} onClick={() => setDays(d)}>{d} days</button>)}</div>
  </div>
    {loading && <p className="import-hint" data-testid="trends-loading-hint">Loading trends…</p>}
    <section className="panel chart-panel" data-testid="trends-stock-chart">
      <div className="panel-heading"><div><h2>Stock levels by category</h2><p>Quantity on hand at the end of each day</p></div><TrendingUp size={21} className="muted-icon" /></div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data.stock_levels} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e3ebe0" />
          <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} stroke="#8a998a" interval="preserveStartEnd" minTickGap={24} />
          <YAxis fontSize={11} stroke="#8a998a" />
          <Tooltip labelFormatter={fmtDate} contentStyle={{ borderRadius: 8, border: "1px solid #dde7da", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="Raw Material" stroke="#2e6b31" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="Finished Product" stroke="#d98a3d" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="Packaging Material" stroke="#3d7ad9" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </section>
    <section className="panel chart-panel" data-testid="trends-dispatch-chart">
      <div className="panel-heading"><div><h2>Dispatch volume</h2><p>Quantity dispatched per day</p></div><Truck size={21} className="muted-icon" /></div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data.dispatch_volume} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e3ebe0" />
          <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} stroke="#8a998a" interval="preserveStartEnd" minTickGap={24} />
          <YAxis fontSize={11} stroke="#8a998a" />
          <Tooltip labelFormatter={fmtDate} contentStyle={{ borderRadius: 8, border: "1px solid #dde7da", fontSize: 12 }}
            formatter={(value, name) => [value, name === "quantity" ? "Quantity dispatched" : "Challans"]} />
          <Bar dataKey="quantity" fill="#2e6b31" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </section>
    <section className="panel chart-panel" data-testid="trends-kitchen-usage-chart">
      <div className="panel-heading"><div><h2>Raw material usage by kitchen section</h2><p>Quantity issued to each chef / kitchen section per day</p></div><ChefHat size={21} className="muted-icon" /></div>
      {kitchenData.sections.length === 0 ? <p className="import-hint">No kitchen requisitions logged in this range yet.</p> :
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={kitchenData.usage_by_day} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e3ebe0" />
            <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} stroke="#8a998a" interval="preserveStartEnd" minTickGap={24} />
            <YAxis fontSize={11} stroke="#8a998a" />
            <Tooltip labelFormatter={fmtDate} contentStyle={{ borderRadius: 8, border: "1px solid #dde7da", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} payload={kitchenData.sections.map((section, i) => ({ value: section, type: "square", color: KITCHEN_COLORS[i % KITCHEN_COLORS.length] }))} />
            {kitchenData.sections.map((section, i) => <Bar key={section} dataKey={(row) => row[section]} name={section} stackId="kitchen" fill={KITCHEN_COLORS[i % KITCHEN_COLORS.length]} radius={i === kitchenData.sections.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />)}
          </BarChart>
        </ResponsiveContainer>}
    </section>
  </div>;
}
